import type {
    MembershipIncome,
    MembershipIncomeUpsertDto,
} from '@fruitful/member-profile';
import React from 'react';
import {
    BodySmallRegular,
    CirclePlusIcon,
    H6Emphasized,
    LabelRegular,
    Shadows,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../../common/components/GroveButtons';
import type { MembershipDetails } from '../../../entities/entityTypes';
import { MainIncomeNewRow } from './MainIncomeNewRow';
import { MainIncomeTableRow } from './MainIncomeTableRow';
import type {
    IncomeTypePickerItem,
    OwnerPickerItem,
    PayCyclePickerItem,
} from './moneyMapperIncomeForm';
import {
    INCOME_TABLE_ACTIONS_OUTER_WIDTH,
    INCOME_TABLE_CARD_EDGE_PADDING,
    INCOME_TABLE_ROW_PADDING_HORIZONTAL,
    incomeTableAmountColumnProps,
    incomeTableIncomeTypeColumnProps,
    incomeTableMemberColumnProps,
    incomeTablePayCycleColumnProps,
    incomeTableSourceColumnProps,
} from './moneyMapperIncomeTableLayout';

export interface MainIncomeSourcesCardProps {
    membership: MembershipDetails;
    incomes: MembershipIncome[];
    showNewIncomeRow: boolean;
    onShowNewRow: () => void;
    onCancelNewRow: () => void;
    ownerOptions: Record<string, string>;
    ownerPickerItems: OwnerPickerItem[];
    payCyclePickerItems: PayCyclePickerItem[];
    incomeTypePickerItems: IncomeTypePickerItem[];
    canMutateIncomes: boolean;
    isSaving: boolean;
    isSubmittingIncome: boolean;
    onOpenIncomeDetails: (incomeId: string) => void;
    upsertMembershipIncomeAsync: (
        membershipId: string,
        body: MembershipIncomeUpsertDto[],
    ) => Promise<unknown>;
    refetchMembershipAndSyncMoneyMap: () => Promise<void>;
}

export const MainIncomeSourcesCard = ({
    membership,
    incomes,
    showNewIncomeRow,
    onShowNewRow,
    onCancelNewRow,
    ownerOptions,
    ownerPickerItems,
    payCyclePickerItems,
    incomeTypePickerItems,
    canMutateIncomes,
    isSaving,
    isSubmittingIncome,
    onOpenIncomeDetails,
    upsertMembershipIncomeAsync,
    refetchMembershipAndSyncMoneyMap,
}: MainIncomeSourcesCardProps) => {
    const isEmptyMainIncome = incomes.length === 0 && !showNewIncomeRow;
    const showTableHeaders = incomes.length > 0 || showNewIncomeRow;
    const showMemberDropdown = ownerPickerItems.length > 1;

    return (
        <YStack
            backgroundColor="$white"
            width="100%"
            borderRadius={24}
            paddingTop={24}
            paddingBottom={24}
            gap={0}
            {...Shadows.SMALL}
        >
            <YStack paddingHorizontal={INCOME_TABLE_CARD_EDGE_PADDING}>
                <H6Emphasized color="$gray1200">Main income</H6Emphasized>
                <BodySmallRegular color="$gray1000" marginBottom={12}>
                    Accounted in the monthly total.
                </BodySmallRegular>
            </YStack>

            {showTableHeaders && (
                <XStack
                    width="100%"
                    alignItems="flex-end"
                    paddingVertical={12}
                    paddingHorizontal={INCOME_TABLE_ROW_PADDING_HORIZONTAL}
                    gap={8}
                >
                    {showMemberDropdown && (
                        <YStack {...incomeTableMemberColumnProps}>
                            <LabelRegular color="$gray1000">
                                Member
                            </LabelRegular>
                        </YStack>
                    )}
                    <YStack {...incomeTableSourceColumnProps}>
                        <LabelRegular color="$gray1000">Source</LabelRegular>
                    </YStack>
                    <YStack {...incomeTableAmountColumnProps}>
                        <LabelRegular color="$gray1000">Amount</LabelRegular>
                    </YStack>
                    <YStack {...incomeTablePayCycleColumnProps}>
                        <LabelRegular color="$gray1000">
                            Pay frequency
                        </LabelRegular>
                    </YStack>
                    <YStack {...incomeTableIncomeTypeColumnProps}>
                        <LabelRegular color="$gray1000">
                            Income type
                        </LabelRegular>
                    </YStack>
                    <XStack
                        width={INCOME_TABLE_ACTIONS_OUTER_WIDTH}
                        flexShrink={0}
                    />
                </XStack>
            )}

            {!isEmptyMainIncome && (
                <>
                    {incomes.map((income) => (
                        <MainIncomeTableRow
                            key={income.id}
                            income={income}
                            membershipId={membership.id}
                            ownerPickerItems={ownerPickerItems}
                            payCyclePickerItems={payCyclePickerItems}
                            incomeTypePickerItems={incomeTypePickerItems}
                            canMutate={canMutateIncomes}
                            moneyMapSaving={isSaving}
                            isModalBusy={isSubmittingIncome}
                            onOpenDetails={() => {
                                onOpenIncomeDetails(income.id);
                            }}
                            upsertMembershipIncomeAsync={
                                upsertMembershipIncomeAsync
                            }
                            refetchMembershipAndSyncMoneyMap={
                                refetchMembershipAndSyncMoneyMap
                            }
                        />
                    ))}
                    {showNewIncomeRow && (
                        <MainIncomeNewRow
                            membership={membership}
                            ownerOptions={ownerOptions}
                            ownerPickerItems={ownerPickerItems}
                            payCyclePickerItems={payCyclePickerItems}
                            incomeTypePickerItems={incomeTypePickerItems}
                            canMutate={canMutateIncomes}
                            moneyMapSaving={isSaving}
                            isModalBusy={isSubmittingIncome}
                            upsertMembershipIncomeAsync={
                                upsertMembershipIncomeAsync
                            }
                            refetchMembershipAndSyncMoneyMap={
                                refetchMembershipAndSyncMoneyMap
                            }
                            onSaved={onCancelNewRow}
                        />
                    )}
                </>
            )}

            <XStack
                width="100%"
                justifyContent="flex-start"
                alignItems="center"
                gap={16}
                paddingHorizontal={INCOME_TABLE_CARD_EDGE_PADDING}
                paddingTop={isEmptyMainIncome ? 20 : 16}
                paddingBottom={4}
                flexWrap="wrap"
            >
                {showNewIncomeRow ? (
                    <Button
                        variant="secondary"
                        size="large"
                        disabled={!canMutateIncomes || isSaving}
                        onPress={onCancelNewRow}
                    >
                        Cancel
                    </Button>
                ) : (
                    <Button
                        variant="secondary"
                        size="large"
                        icon={CirclePlusIcon}
                        disabled={
                            !canMutateIncomes || isSaving || showNewIncomeRow
                        }
                        onPress={onShowNewRow}
                    >
                        Add income source
                    </Button>
                )}
            </XStack>
        </YStack>
    );
};
